// @flow

import { all, put, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import config from "@config";
import actionCreators from "@modules/employee/actionCreator";
import recordingActionCreators from "@modules/recording/actionCreator";
import {
  ResetUserAccountPassword,
  GetUserRecentLogins,
  GetUserAccountChangelog,
  GetAccessLevelSchema,
  UpdateAccessLevel,
  GetUserAccountAssignments,
  UpdateUserAccountAssignments,
  GetSavedReports,
} from "@modules/employee/constants/actions";
import type { HierarchyAssignment, HierarchyAssignmentSummary } from "@modules/service/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import type { GridSortType } from "@components/grid-table/grid-table.js";
import { sortToString } from "@transformers/sort";
import { parseSchema } from "@utils/schema";

type UserAccountRequest = $Exact<{
  clientId?: string,
  userId: string,
}>;

export function* onResetUserAccountPassword({
  payload,
}: Action<UserAccountRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.userId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(http.post, `/user/${payload.userId}/password-reset`);
    yield all([
      put(actionCreators.ResetUserAccountPasswordSuccess(response.data)),
      put(sendSuccessMessage("EMAIL_PASSWORD_INSTRUCTIONS_SENT")),
    ]);
  } catch (e) {
    yield put(actionCreators.ResetUserAccountPasswordError(e.response));
  }
}

export function* onGetUserRecentLogins({
  payload,
}: Action<UserAccountRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.userId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(http.get, `/user/${payload.userId}/login/activity`, {
      params: {
        limit: config.resultsPerPage,
      },
    });

    yield put(actionCreators.GetUserRecentLoginsSuccess(response.data.content || []));
  } catch (e) {
    yield put(actionCreators.GetUserRecentLoginsError(e.response));
  }
}

export function* onGetUserAccountChangelog({
  payload,
}: Action<UserAccountRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.userId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(http.get, `/user/${payload.userId}/changelog`, {
      params: {
        limit: config.resultsPerPage,
      },
    });

    yield put(actionCreators.GetUserAccountChangelogSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetUserAccountChangelogError(e.response));
  }
}

type GetAccessLevelSchemaRequest = $Exact<{
  clientId: ?number,
  employeeId: ?number,
}>;

export function* onGetAccessLevelSchema({
  payload,
}: Action<GetAccessLevelSchemaRequest>): Generator<*, void, *> {
  try {
    const clientId = (payload || {}).clientId;
    const employeeId = (payload || {}).employeeId;
    if (!payload || !clientId || !employeeId) {
      throw Error("Invalid payload provided");
    }

    const schema = yield parseSchema("EmployeeAccessScopeDTO", { clientId: payload.clientId });
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/access-scope`,
    );

    yield all([
      put(actionCreators.GetAccessLevelSchemaSuccess(schema)),
      put(actionCreators.GetAccessLevelValueSuccess(response.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetAccessLevelSchemaError(e.response));
  }
}

type UpdateAccessLevelRequest = $Exact<{
  clientId: ?number,
  employeeId: ?number,
  data: ?Object,
}>;

export function* onUpdateAccessLevel({
  payload,
}: Action<UpdateAccessLevelRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/access-scope`,
      payload.data,
    );

    yield all([
      put(actionCreators.UpdateAccessLevelSuccess(response.data)),
      put(recordingActionCreators.UpdateRecordingAccessLevelSuccess(response.data)),
      put(actionCreators.ToggleEditAccessLevelModal(false)),
      put(sendSuccessMessage("USER_ACCESS_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateAccessLevelError(e.response));
  }
}

export function* onGetUserAccountAssignments({
  payload,
}: Action<UpdateAccessLevelRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/access-scope`,
    );
    yield put(actionCreators.GetUserAccountAssignmentsSuccess(response.data.hierarchyAssignments));
  } catch (e) {
    yield put(actionCreators.GetUserAccountAssignmentsError(e.response));
  }
}

type UpdateUserAccountAssignmentsRequest = $Exact<{
  clientId: number,
  employeeId: number,
  data: $Exact<{
    assignments: Array<HierarchyAssignment>,
    userType: string,
  }>,
}>;

export function* onUpdateUserAccountAssignments({
  payload,
}: Action<UpdateUserAccountAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/access-scope`,
    );

    if (
      prevAssignments &&
      prevAssignments.hierarchyAssignments &&
      prevAssignments.hierarchyAssignments.length
    ) {
      yield all([
        ...prevAssignments.hierarchyAssignments.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/access-scope/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.data.assignments.length) {
      yield all([
        ...payload.data.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/access-scope/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(
        actionCreators.GetUserAccountAssignments({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(actionCreators.ToggleUserAccountAssignmentsModal(false)),
      put(actionCreators.UpdateUserAccountAssignmentsSuccess()),
      put(sendSuccessMessage("AREA_OF_ACCESS_SUCCESSFULLY_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateUserAccountAssignmentsError(e.response));
  }
}

type SavedReportsRequest = $Exact<{
  clientId: string,
  userId: string,
  sort?: GridSortType,
}>;

export function* onGetSavedReports({
  payload,
}: Action<SavedReportsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.userId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/reports/client/${payload.clientId}/reports/saved-reports/${payload.userId}`,
      {
        params: {
          page: 1,
          sort: sortToString(payload.sort || {}),
          limit: config.results100PerPage,
        },
      },
    );

    yield put(actionCreators.GetSavedReportsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetSavedReportsError(e.response));
  }
}

export function* UserAccountSagas(): Generator<*, void, *> {
  yield all([
    takeEvery(ResetUserAccountPassword, onResetUserAccountPassword),
    takeEvery(GetUserRecentLogins, onGetUserRecentLogins),
    takeEvery(GetUserAccountChangelog, onGetUserAccountChangelog),
    takeEvery(GetAccessLevelSchema, onGetAccessLevelSchema),
    takeEvery(UpdateAccessLevel, onUpdateAccessLevel),
    takeEvery(GetUserAccountAssignments, onGetUserAccountAssignments),
    takeEvery(UpdateUserAccountAssignments, onUpdateUserAccountAssignments),
    takeEvery(GetSavedReports, onGetSavedReports),
  ]);
}

export default UserAccountSagas;
